// src/services/incomeService.ts

import { API_CONFIG } from '../config/apiConfig';

// --- INTERFACES ---
// Mirrors the structure of AssetData, adapted for Income
export interface IncomeData {
  id: string; // The local UUID, which will be mapped to income_id in Joget
  incomeOwnerType: string;
  incomeType: string;
  incomeSubType: string;
  incomeName: string;
  incomeAmount: string;
  incomeFrequency: string;
  totalIncomeAmount: string;
  totalFamilyIncome: string;
  status?: string;
  familyMemberId?: string;
  jogetId?: string; // Optional: To store the original Joget PK
}

// Mirrors the structure of AssetsApiResponse for the backend's paginated response
export interface IncomesApiResponse {
  status: string;
  data: IncomeData[];
  pagination: {
    total: number;
    size: number;
    currentPage: number;
  };
}


// --- AUTHENTICATION HELPER ---
// Standardized helper to get the JWT Bearer token
const getAuthToken = (): string | null => {
  const token = localStorage.getItem('authToken');
  if (!token) {
    console.error('Authentication Error: JWT Token not found in localStorage.');
    return null;
  }
  return token;
};


// --- API FUNCTIONS ---

/**
 * Fetches all income records for the logged-in user from your Node.js backend.
 */
export const fetchIncomesData = async (page = 1, size = 10): Promise<IncomesApiResponse> => {
  const token = getAuthToken();
  if (!token) throw new Error('User not logged in.');

  try {
    const response = await fetch(`${API_CONFIG.API_NODE_BASE_URL}/api/income?page=${page}&size=${size}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
        const errorBody = await response.json();
        throw new Error(errorBody.message || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();

  } catch (error) {
    console.error('Error fetching income data:', error);
    throw error;
  }
};


/**
 * Creates or updates an income record via your Node.js backend.
 */
export const saveOrUpdateIncomeRecord = async (income: IncomeData): Promise<any> => {
  const token = getAuthToken();
  if (!token) throw new Error('User not logged in.');

  // Construct the payload exactly as the backend controller expects
  const payload = {
    id: income.id || null, 
    status: income.status || 'active',
    request_data: {
      incomeOwnerType: income.incomeOwnerType,
      incomeType: income.incomeType,
      incomeSubType: income.incomeSubType,
      incomeName: income.incomeName,
      incomeAmount: income.incomeAmount,
      incomeFrequency: income.incomeFrequency,
      totalIncomeAmount: income.totalIncomeAmount,
      totalFamilyIncome: income.totalFamilyIncome,
      familyMemberId: income.familyMemberId,
    }
  };
  
  // Clean up undefined familyMemberId before sending
  if (payload.request_data.familyMemberId === '' || payload.request_data.familyMemberId === undefined) {
      delete payload.request_data.familyMemberId;
  }

  try {
    const response = await fetch(`${API_CONFIG.API_NODE_BASE_URL}/api/income`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Error saving income record:`, error);
    throw error;
  }
};


/**
 * Fetches the list of family members (dependents) for the logged-in user.
 * This is a shared utility function, useful for both assets and income forms.
 */
export const fetchDependents = async (): Promise<{ id: string; name: string }[]> => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('User is not authenticated.');
  }

  try {
    const response = await fetch(`${API_CONFIG.API_NODE_BASE_URL}/api/profile/dependents`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorBody = await response.json();
      throw new Error(errorBody.message || 'Failed to fetch dependents');
    }

    const result = await response.json();
    return result.data || [];
    
  } catch (error) {
    console.error('Error fetching dependents data:', error);
    throw error;
  }
};

/**
 * Soft deletes an income record by its ID via your Node.js backend.
 */
export const deleteIncomeRecord = async (id: string): Promise<{ success: boolean; message: string }> => {
  const token = getAuthToken();
  if (!token) throw new Error('User not logged in.');

  try {
    const response = await fetch(`${API_CONFIG.API_NODE_BASE_URL}/api/income/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return { success: true, message: result.message };
  } catch (error: any) {
    console.error('Error deleting income record:', error);
    return { 
      success: false, 
      message: error.message || 'Failed to delete income record.' 
    };
  }
};